import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { errorToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { DateUtils } from '@/utils/Date.utils';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { modalAction } from '@/utils/modal';
import { type FC, type FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

interface UpdateDOBProps {
  subjects: Array<Animal>;
  studyApiId: StudyApiId;
  closeModal: () => void;
  handleCallback: (message: string) => void;
}

const UpdateDOB: FC<UpdateDOBProps> = ({ subjects, studyApiId, closeModal, handleCallback }) => {
  const dateNowIso = DateUtils.dateNow();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;

  const [date, setDate] = useState(dateNowIso);
  const [validationError, setValidationError] = useState('');

  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const {
    invoke: updateDob,
    loading,
    response: updateResponse,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/dob',
    options: { invokeOnInit: false, onError: { throw: false, capture: true, toast: false, slug: 'animal-dob-update' } },
  });

  const saveDob = async (reasonForChange?: string) => {
    const animalApiIds = subjects.map((s) => s.api_id!);
    const result = await updateDob({
      path: { studyApiId },
      body: {
        animal_api_ids: animalApiIds,
        dob: _isEmpty(date) ? null : date,
        ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
      },
    });
    if (result.type === 'success') {
      handleCallback('Successfully updated date of birth');
    } else if (reasonForChangeEnabled) {
      // The reason-for-change modal has replaced this modal, so the inline ApiErrorBanner
      // is unmounted; surface the failure via a toast that survives the unmount.
      errorToast('There was a problem updating the date of birth');
    }
  };

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();

    if (isDobRequired && _isEmpty(date)) {
      setValidationError('DOB is required');
      return;
    }

    setValidationError('');
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveDob(reasonForChange),
      });
      return;
    }
    await saveDob();
  };

  const what = subjects.length > 1 ? 'animals' : 'animal';
  const whatDemonstrative = what === 'animals' ? 'these' : 'this';
  const message = `Update the date of birth for ${whatDemonstrative} ${what}?`;

  return (
    <form onSubmit={submitForm} className="ui__narrow-modal">
      <div className="mb3 tc">
        <h3 className="normal f4 pb2">{message}</h3>
      </div>
      {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
      <div className="mb4">
        <label htmlFor="dob">Date of birth</label>
        {validationError && <p className="red f6 mt2 mb0">{validationError}</p>}
        <DatePickerNative
          value={date}
          onChange={(value: string) => {
            setDate(value);
            setValidationError('');
          }}
          max={dateNowIso}
          style={{ marginBottom: 0 }}
        />
      </div>
      <div>
        <Button submit disabled={loading} className="w-100 mb2" testId="update-dob__save">
          Save
        </Button>
        <Button plain className="w-100" onClick={() => closeModal()}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default UpdateDOB;
